import { useCallback, useEffect, useRef, useState } from 'react';
import {
  respondToScalingRulersExerciseStream,
  retryScalingRulersExercise,
  startScalingRulersExercise,
  streamScalingRulersEvaluation,
} from '../services/api';

const CLIENT_STREAM_FRAME_MS = 24;

function createClientReplyRenderer(render) {
  let target = '';
  let displayed = '';
  let timer = null;
  let completionResolvers = [];

  const resolveCompletions = () => {
    const resolvers = completionResolvers;
    completionResolvers = [];
    resolvers.forEach((resolve) => resolve());
  };

  const tick = () => {
    timer = null;
    if (displayed.length < target.length) {
      const remaining = target.length - displayed.length;
      const step = remaining > 60 ? 8 : remaining > 24 ? 4 : 2;
      displayed = target.slice(0, displayed.length + step);
      render(displayed);
    }

    if (displayed.length < target.length) {
      timer = window.setTimeout(tick, CLIENT_STREAM_FRAME_MS);
    } else {
      resolveCompletions();
    }
  };

  const schedule = () => {
    if (timer === null && displayed.length < target.length) {
      tick();
    }
  };

  return {
    push(nextTarget) {
      target = String(nextTarget || '');
      schedule();
    },
    finish(finalTarget) {
      target = String(finalTarget || target);
      schedule();
      if (displayed.length >= target.length) return Promise.resolve();
      return new Promise((resolve) => completionResolvers.push(resolve));
    },
    cancel() {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      resolveCompletions();
    },
  };
}

function useSpecificStrategiesFlow() {
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [exercise, setExercise] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [stage, setStage] = useState(null);
  const [status, setStatus] = useState('idle');
  const [evaluation, setEvaluation] = useState(null);
  const [automitiLabels, setAutomitiLabels] = useState([]);
  const [classifierExplanation, setClassifierExplanation] = useState('');
  const [turnResults, setTurnResults] = useState([]);
  const [viewedTurnIndex, setViewedTurnIndex] = useState(null);
  const [revealedGuidanceTurnIndexes, setRevealedGuidanceTurnIndexes] = useState([]);
  const [interimGuidanceVisible, setInterimGuidanceVisible] = useState(false);
  const [completionReason, setCompletionReason] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [strategyInfoOpen, setStrategyInfoOpen] = useState(false);
  const [strategyInfoShowCheckbox, setStrategyInfoShowCheckbox] = useState(false);
  const [dismissStrategyPopups, setDismissStrategyPopups] = useState(false);
  const activeEvaluationStreamRef = useRef(null);
  const activeClientStreamRef = useRef(null);
  const historyHydrationAttemptRef = useRef(null);

  useEffect(() => () => {
    activeClientStreamRef.current?.abort();
    activeEvaluationStreamRef.current?.abort();
  }, []);

  useEffect(() => {
    if (
      status !== 'complete'
      || !attemptId
      || turnResults.length >= 2
      || historyHydrationAttemptRef.current === attemptId
    ) return;

    historyHydrationAttemptRef.current = attemptId;
    const stream = streamScalingRulersEvaluation(attemptId, {
      onComplete: (completed) => {
        if (completed.turn_results?.length) {
          setTurnResults(completed.turn_results);
        }
        setCompletionReason(completed.completion_reason || null);
        setOutcome(completed.outcome || null);
      },
    });
    stream.promise.catch((err) => {
      console.error('Failed to load Scaling Questions turn history:', err);
      historyHydrationAttemptRef.current = null;
    });
  }, [attemptId, status, turnResults.length]);

  const resetExercise = useCallback(() => {
    activeEvaluationStreamRef.current?.abort();
    activeEvaluationStreamRef.current = null;
    activeClientStreamRef.current?.abort();
    activeClientStreamRef.current = null;
    setAttemptId(null);
    setExercise(null);
    setConversation([]);
    setStage(null);
    setStatus('idle');
    setEvaluation(null);
    setAutomitiLabels([]);
    setClassifierExplanation('');
    setTurnResults([]);
    setViewedTurnIndex(null);
    setRevealedGuidanceTurnIndexes([]);
    setInterimGuidanceVisible(false);
    setCompletionReason(null);
    setOutcome(null);
    setLoading(false);
    setError('');
  }, []);

  const resetFlow = useCallback(() => {
    setSelectedTask(null);
    setSelectedStrategy(null);
    setStrategyInfoOpen(false);
    setStrategyInfoShowCheckbox(false);
    resetExercise();
  }, [resetExercise]);

  const openStrategyInfo = useCallback(() => {
    setStrategyInfoShowCheckbox(false);
    setStrategyInfoOpen(true);
  }, []);

  const closeStrategyInfo = useCallback(() => {
    setStrategyInfoOpen(false);
    setStrategyInfoShowCheckbox(false);
  }, []);

  const loadReplay = useCallback((replay) => {
    resetExercise();
    setStrategyInfoOpen(false);
    setStrategyInfoShowCheckbox(false);
    setSelectedTask(replay.task || 'Evoking');
    setSelectedStrategy(replay.strategy || 'Scaling Questions');
    setAttemptId(replay.attempt_id);
    setExercise(replay.exercise || null);
    setConversation(replay.conversation || []);
    setStage(replay.stage || null);
    setStatus('complete');
    setEvaluation(replay.evaluation || null);
    setAutomitiLabels(replay.automiti_labels || []);
    setClassifierExplanation(replay.classifier_explanation || '');
    setTurnResults(replay.turn_results || []);
    setViewedTurnIndex(null);
    setCompletionReason(replay.completion_reason || null);
    setOutcome(replay.outcome || null);
  }, [resetExercise]);

  const selectTask = useCallback((task) => {
    setSelectedTask(task);
    setSelectedStrategy(null);
    resetExercise();
  }, [resetExercise]);

  const selectStrategy = useCallback(async (strategy) => {
    const shouldShowInfo = (
      strategy === 'Scaling Questions'
      && strategy !== selectedStrategy
      && !dismissStrategyPopups
    );
    setSelectedStrategy(strategy);
    resetExercise();
    if (strategy !== 'Scaling Questions') return;

    if (shouldShowInfo) {
      setStrategyInfoShowCheckbox(true);
      setStrategyInfoOpen(true);
    }

    setLoading(true);
    setStatus('loading');
    try {
      const data = await startScalingRulersExercise();
      setAttemptId(data.attempt_id);
      setExercise(data.exercise);
      setConversation(data.conversation || []);
      setStage(data.stage);
      setStatus(data.status || 'active');
    } catch (err) {
      console.error('Failed to start Scaling Questions exercise:', err);
      setError(err?.response?.data?.error || 'Failed to start the exercise. Please try again.');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  }, [dismissStrategyPopups, resetExercise, selectedStrategy]);

  const retryExercise = useCallback(async () => {
    if (!attemptId || loading || status !== 'complete') return;

    setLoading(true);
    setError('');
    try {
      const data = await retryScalingRulersExercise(attemptId);
      setAttemptId(data.attempt_id);
      setExercise(data.exercise);
      setConversation(data.conversation || []);
      setStage(data.stage);
      setStatus(data.status || 'active');
      setEvaluation(null);
      setAutomitiLabels([]);
      setClassifierExplanation('');
      setTurnResults([]);
      setViewedTurnIndex(null);
      setRevealedGuidanceTurnIndexes([]);
      setInterimGuidanceVisible(false);
      setCompletionReason(null);
      setOutcome(null);
    } catch (err) {
      console.error('Failed to retry Scaling Questions exercise:', err);
      setError(err?.response?.data?.error || 'Failed to retry the exercise. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [attemptId, loading, status]);

  const submitResponse = useCallback(async (message) => {
    if (!attemptId || !message.trim() || loading || status !== 'active') return;
    const submittedMessage = message.trim();
    const previousConversation = conversation;
    const submittedConversation = [
      ...previousConversation,
      { speaker: 'counsellor', text: submittedMessage },
      { speaker: 'client', text: '', isStreaming: true },
    ];
    setConversation(submittedConversation);
    setAutomitiLabels([]);
    setClassifierExplanation('');
    setEvaluation(null);
    setViewedTurnIndex(null);
    setLoading(true);
    setError('');
    let acceptedResponse = null;
    const clientReplyRenderer = createClientReplyRenderer((reply) => {
      setConversation([
        ...submittedConversation.slice(0, -1),
        { speaker: 'client', text: reply, isStreaming: true },
      ]);
    });
    try {
      const clientStream = respondToScalingRulersExerciseStream(attemptId, submittedMessage, {
        onClassification: (classification) => {
          setAutomitiLabels(classification.automiti_labels || []);
          setClassifierExplanation(classification.classifier_explanation || '');
        },
        onClientReply: (reply) => {
          clientReplyRenderer.push(reply);
        },
      });
      activeClientStreamRef.current = clientStream;
      const data = await clientStream.promise;
      activeClientStreamRef.current = null;
      acceptedResponse = data;
      await clientReplyRenderer.finish(data.client_reply);
      setConversation(data.conversation || []);
      setStage(data.stage);
      setStatus(data.status || 'active');
      setEvaluation(data.evaluation || null);
      setAutomitiLabels(data.automiti_labels || []);
      setClassifierExplanation(data.classifier_explanation || '');
      setCompletionReason(data.completion_reason || null);
      setOutcome(data.outcome || null);

      if (data.requires_evaluation) {
        const stream = streamScalingRulersEvaluation(attemptId, {
          onGuidance: (feedback) => setEvaluation({ feedback }),
          onComplete: (completed) => {
            setEvaluation(completed.evaluation || null);
            setStatus(completed.status || 'complete');
            setCompletionReason(completed.completion_reason || data.completion_reason || null);
            setOutcome(completed.outcome || data.outcome || null);
          },
        });
        activeEvaluationStreamRef.current = stream;
        const completed = await stream.promise;
        activeEvaluationStreamRef.current = null;
        setEvaluation(completed.evaluation || null);
        setStatus(completed.status || 'complete');
        setCompletionReason(completed.completion_reason || data.completion_reason || null);
        setOutcome(completed.outcome || data.outcome || null);
        setTurnResults((previous) => completed.turn_results?.length
          ? completed.turn_results
          : [
            ...previous,
            {
              trainee_turn_index: previous.length,
              trainee_text: submittedMessage,
              automiti_labels: data.automiti_labels || [],
              classifier_explanation: data.classifier_explanation || '',
              evaluation: completed.evaluation || null,
            },
          ]);
      }
      return data;
    } catch (err) {
      console.error('Failed to continue Scaling Questions exercise:', err);
      if (!acceptedResponse) {
        setConversation(previousConversation);
      }
      setError(err?.response?.data?.error || 'Failed to continue the exercise. Please try again.');
      return acceptedResponse;
    } finally {
      clientReplyRenderer.cancel();
      activeEvaluationStreamRef.current = null;
      activeClientStreamRef.current = null;
      setLoading(false);
    }
  }, [attemptId, conversation, loading, status]);

  const revealGuidanceTurn = useCallback((turnIndex) => {
    if (!Number.isInteger(turnIndex) || turnIndex < 0) return;
    setRevealedGuidanceTurnIndexes((current) => (
      current.includes(turnIndex) ? current : [...current, turnIndex]
    ));
    setInterimGuidanceVisible(true);
  }, []);

  const hideInterimGuidance = useCallback(() => {
    setInterimGuidanceVisible(false);
  }, []);

  return {
    selectedTask,
    selectedStrategy,
    attemptId,
    exercise,
    conversation,
    stage,
    status,
    evaluation,
    automitiLabels,
    classifierExplanation,
    turnResults,
    viewedTurnIndex,
    revealedGuidanceTurnIndexes,
    interimGuidanceVisible,
    completionReason,
    outcome,
    loading,
    error,
    strategyInfoOpen,
    strategyInfoShowCheckbox,
    dismissStrategyPopups,
    setDismissStrategyPopups,
    openStrategyInfo,
    closeStrategyInfo,
    selectTask,
    selectStrategy,
    retryExercise,
    submitResponse,
    setViewedTurnIndex,
    revealGuidanceTurn,
    hideInterimGuidance,
    loadReplay,
    resetExercise,
    resetFlow,
  };
}

export default useSpecificStrategiesFlow;
